import { useCallback, useEffect, useRef, useState } from 'react';
import RotateCW from '@spectrum-icons/workflow/RotateCW';
import ShoppingCart from '@spectrum-icons/workflow/ShoppingCart';
import ZoomIn from '@spectrum-icons/workflow/ZoomIn';

import { AffiliateLink } from '../../../affiliates/components/AffiliateLink';
import { truncateToOneDecimal } from '../cardStats/formatters';
import { renderManaText } from '../cardStats/manaText';
import { ReportBand } from '../report/ReportBand';
import { SheetHeader } from '../sheetHeader/SheetHeader';
import { CardEffects } from './CardEffects';
import { CardPrintings } from './CardPrintings';
import { CardScoringSection } from './CardScoringSection';
import { ChipList, Section } from './Section';
import { useStickyOffsetVar } from './useStickyOffsetVar';
import '../report/report.css';
import '../../filterpanel/mana-cost.css';
import './cardInfoDialog.css';

/**
 * The card's headline numbers. Only what the payload actually carries gets a
 * tile — a card with no price or no roles shows fewer of them rather than
 * placeholder zeroes.
 */
function cardTiles({ card, printsLoading }) {
    const tiles = [];
    if (card.cmc != null) {
        tiles.push({
            key: 'manaValue',
            label: 'Mana value',
            value: String(card.cmc),
            graphic: card.manaCost ? (
                <span className="cardInfoTileMana" role="img" aria-label={`Mana cost ${card.manaCost}`}>
                    {renderManaText(card.manaCost, 'small')}
                </span>
            ) : null,
        });
    }
    if (card.roles.length > 0) {
        tiles.push({
            key: 'roles',
            label: 'Roles',
            value: String(card.roles.length),
            sub: card.roles.map((role) => role.label).join(' · '),
        });
    }
    if (card.salt.total != null) {
        tiles.push({
            key: 'salt',
            label: 'Salt',
            value: truncateToOneDecimal(card.salt.total),
            sub: card.salt.tierLabel,
        });
    }
    if (card.price) {
        tiles.push({
            key: 'price',
            label: 'Price',
            value: card.price.display,
            sub: card.price.isLive ? 'Cheapest printing' : 'Cached price',
        });
    } else if (printsLoading) {
        tiles.push({
            key: 'price',
            label: 'Price',
            value: <span className="skeletonBlock cardInfoValueSkeleton">&nbsp;</span>,
        });
    }
    return tiles;
}

/** The buy button's tooltip, which also names the affiliate relationship. */
function buyTitle(card) {
    if (!card.price) return `Search TCGPlayer for ${card.name} — affiliate link, supports the site`;
    const print = card.price.print;
    const where = print ? ` (${(print.set || '').toUpperCase()} #${print.collectorNumber})` : '';
    return `Buy ${card.name}${where} on TCGPlayer for ${card.price.display} — affiliate link, supports the site`;
}

/**
 * The whole card report: identity and close control on top, verdict band with
 * the card's headline numbers, then the card image beside one reading flow of
 * sections. Everything below the band scrolls as a single document.
 */
export function CardReport({ card, prints, printsLoading, isLoading, error, titleId, onClose, onFlip }) {
    const printingsRef = useRef(null);
    // The card image parks under the sticky buy row on wide layouts, so that
    // row's real height travels to the CSS as `--cardinfo-buy-h`.
    const { hostRef: buyHostRef, measuredRef: buyBarRef } = useStickyOffsetVar('--cardinfo-buy-h');
    // On wide layouts the card's column scrolls on its own, sized to the
    // scroller's visible height, so the scroller publishes that too.
    const { hostRef: viewHostRef, measuredRef: viewRef } = useStickyOffsetVar('--cardinfo-view-h');
    const scrollRef = useCallback(
        (el) => {
            buyHostRef(el);
            viewHostRef(el);
            viewRef(el);
        },
        [buyHostRef, viewHostRef, viewRef]
    );
    const tiles = cardTiles({ card, printsLoading });
    const showDetail = !error;
    const typeLine = [card.typeLine, card.statLine].filter(Boolean).join(' · ');

    return (
        <div className="cardInfoShell">
            <SheetHeader title={card.name} subtitle={typeLine} titleId={titleId} onClose={onClose} />
            <div className="cardInfoScroll report" ref={scrollRef}>
                <ReportBand
                    label={`${card.name} summary`}
                    backdrop={card.artCrop ? [card.artCrop] : null}
                    meta={card.flags.length > 0 ? <CardFlags flags={card.flags} /> : null}
                    tiles={tiles}
                    /* Skeleton tiles for the whole band until the card's
                       numbers land, so the row doesn't fill in one tile at a
                       time as the price and the details arrive separately. */
                    loading={isLoading}
                >
                    {error ? (
                        <p className="cardInfoError" role="alert">
                            {error}
                        </p>
                    ) : null}
                </ReportBand>
                {card.buyTarget && (
                    <div className="cardInfoBuyBar" ref={buyBarRef}>
                        <AffiliateLink
                            provider="tcgplayer"
                            target={card.buyTarget}
                            placement="cheapest-tile"
                            className="searedPrimaryBtn cardInfoBuy"
                            title={buyTitle(card)}
                        >
                            <ShoppingCart size="S" />
                            Buy on TCGPlayer
                            {card.price ? (
                                <span className="cardInfoBuyPrice">· {card.price.display}</span>
                            ) : printsLoading ? (
                                <span className="cardInfoBuyPrice skeletonBlock cardInfoBuySkeleton">&nbsp;</span>
                            ) : null}
                        </AffiliateLink>
                        {prints.length > 1 && (
                            <button
                                type="button"
                                className="buttonReset reportBandLink cardInfoCompare"
                                onClick={() => printingsRef.current?.focus()}
                            >
                                <span>
                                    <span className="cardInfoCompareWord">Compare</span>
                                    {` ${prints.length} printings`}
                                </span>
                            </button>
                        )}
                    </div>
                )}
                <div className="cardInfoBody">
                    <div className="cardInfoFlow">
                        <Section title="Rules text" hint="The card's oracle text.">
                            {card.oracle ? (
                                card.oracle.split('\n').map((line, idx) => (
                                    <p className="cardInfoOracleLine" key={idx}>
                                        {renderManaText(line)}
                                    </p>
                                ))
                            ) : isLoading ? (
                                <p className="skeletonBlock cardInfoLineSkeleton">&nbsp;</p>
                            ) : (
                                <p className="cardInfoEmpty">No rules text on this face.</p>
                            )}
                            {card.keywords.length > 0 && (
                                <ChipList
                                    label="Keywords"
                                    items={card.keywords.map((kw) => ({ key: kw, label: kw }))}
                                />
                            )}
                        </Section>
                        {showDetail && (isLoading || card.roles.length > 0) && (
                            <Section
                                title="Roles"
                                hint="Commandersalt tags this card with these roles — they drive category scoring downstream."
                            >
                                {isLoading && card.roles.length === 0 ? (
                                    <p className="skeletonBlock cardInfoLineSkeleton">&nbsp;</p>
                                ) : (
                                    <ChipList items={card.roles} />
                                )}
                            </Section>
                        )}
                        {showDetail && card.salt.total != null && (
                            <Section title="Saltiness" hint="Where this card's salt score comes from.">
                                {card.salt.entries.length > 0 ? (
                                    <dl className="cardInfoDataList">
                                        {card.salt.entries.map((entry) => (
                                            <div
                                                className="cardInfoDataRow"
                                                key={entry.key}
                                                title={`${entry.label} contributes ${entry.score.toFixed(
                                                    2
                                                )} to this card’s salt.`}
                                            >
                                                <dt>{entry.label}</dt>
                                                <dd>{entry.score.toFixed(1)}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                ) : (
                                    <p className="cardInfoEmpty">No categorised salt signal for this card.</p>
                                )}
                            </Section>
                        )}
                        {showDetail && <CardEffects lines={card.effectLines} loading={isLoading} />}
                        {showDetail && <CardScoringSection scoring={card.scoring} context={card.context} />}
                    </div>
                    {/* The card and its printings, after the reading flow in
                        document order and placed by CSS. On a phone this box
                        dissolves: the card sits beside the rules text and the
                        printings close the scroll. On wide layouts it is the
                        left column and scrolls on its own. */}
                    <div className="cardInfoAside">
                        <CardArt card={card} onFlip={onFlip} />
                        <CardPrintings
                            prints={prints}
                            loading={printsLoading}
                            cardName={card.name}
                            headingRef={printingsRef}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

function CardFlags({ flags }) {
    return (
        <p className="cardInfoFlags">
            {flags.map((flag) => (
                <span className="cardInfoFlag" key={flag.key}>
                    {flag.label}
                </span>
            ))}
        </p>
    );
}

/**
 * The card itself, with its set and artist credit. Narrow layouts show it as a
 * thumbnail beside the rules text so the numbers and text stay above the fold;
 * the zoom control swaps it for a full-width read of the printed card.
 */
function CardArt({ card, onFlip }) {
    const [loaded, setLoaded] = useState(false);
    const [errored, setErrored] = useState(false);
    const [enlarged, setEnlarged] = useState(false);
    const uri = card.imageUri;
    // Re-prime the loader when the source changes so flipping a double-faced
    // card runs the fade-in and the skeleton guard again.
    useEffect(() => {
        setLoaded(false);
        setErrored(false);
    }, [uri]);

    return (
        <figure className={`cardInfoArt${enlarged ? ' is-enlarged' : ''}`}>
            <div className="cardInfoArtFrame">
                {(!uri || (!loaded && !errored)) && <span className="skeletonBlock cardInfoArtSkeleton" />}
                {uri && !errored && (
                    <img
                        src={uri}
                        alt={card.name}
                        className={`cardInfoImage${loaded ? ' is-loaded' : ''}`}
                        onLoad={() => setLoaded(true)}
                        onError={() => setErrored(true)}
                    />
                )}
                {errored && <span className="cardInfoImageFallback">No image for {card.name}</span>}
            </div>
            <div className="cardInfoArtActions">
                <button
                    type="button"
                    className="buttonReset cardInfoArtBtn cardInfoZoom"
                    aria-expanded={enlarged}
                    onClick={() => setEnlarged((open) => !open)}
                >
                    <ZoomIn size="S" />
                    {enlarged ? 'Smaller' : 'Enlarge'}
                </button>
                {card.canFlip && (
                    <button
                        type="button"
                        className="buttonReset cardInfoArtBtn"
                        onClick={onFlip}
                        aria-label={card.nextFaceName ? `Flip card to ${card.nextFaceName}` : 'Flip card'}
                    >
                        <RotateCW size="S" />
                        Flip card
                    </button>
                )}
            </div>
            <figcaption className="cardInfoArtCaption">
                {card.set && (
                    <span>
                        {card.set.name} ({card.set.code.toUpperCase()})
                    </span>
                )}
                {card.artist && <span>Illustrated by {card.artist}</span>}
            </figcaption>
        </figure>
    );
}
